import { useEffect, useSyncExternalStore } from 'react';

import { trpc } from '@/lib/api/trpc';
import { devEnvLabel } from '@/lib/branding/dev-tab';

/** Store name for the dev tab title: `StoreLayout` publishes it, `useBrandHead` reads it. */
let storeName: string | null = null;
const listeners = new Set<() => void>();

const setDevTabStoreName = (name: string | null): void => {
  if (name === storeName) return;
  storeName = name;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useDevTabStoreName = (): string | null => useSyncExternalStore(subscribe, () => storeName);

/** Fetches the store name on local and staging hosts only; production never runs the query. */
export function usePublishDevTabStoreName(storeId: string | undefined): void {
  const isDevHost = devEnvLabel(window.location.hostname) !== null;
  const { data: store } = trpc.stores.getById.useQuery(
    { storeId: storeId ?? '' },
    { enabled: isDevHost && Boolean(storeId) },
  );
  const name = store?.name ?? null;

  useEffect(() => {
    if (!isDevHost) return;
    setDevTabStoreName(name);
    return () => setDevTabStoreName(null);
  }, [isDevHost, name]);
}
